// Non-destructive sync for the whole-store-blob tables (user_inbox, user_quests,
// user_projects, user_programs, user_resources, user_possessions,
// user_acquisitions — all shaped `{user_id, data, updated_at}`).
//
// ─── The bug this replaces ──────────────────────────────────────────────────
// The old pattern READ with a union merge (lib/syncMerge.mergeRecords) but WROTE
// with a wholesale replace (`upsert({ data: { items } })` — the entire local
// array). So a device whose local cache was missing an item DELETED that item
// from the cloud on its next mutation: not "marked deleted", gone, because the
// write replaced the array. Worse, initialize() suppressed its own sync, so a
// merge that CHANGED state was persisted locally but never written back — the
// two sides could not converge on their own. Diagnosed 2026-07-27 against live
// data: the cloud blob held 7 inbox items (all `open`) while localStorage held 8,
// the extra one being the only `routed` item — absent from a cloud row that had
// been written LATER.
//
// ─── What this does instead ─────────────────────────────────────────────────
// 1. READ-MODIFY-WRITE. Every write re-reads the current cloud blob and merges
//    local state INTO it, so a write can only ever ADD or UPDATE — never silently
//    drop another device's records.
// 2. TOMBSTONES. Because writes are now a union, a real delete needs to be
//    represented; otherwise the union resurrects it forever. `remove` records
//    `{id: deletedAt}` instead of just dropping the record. A tombstone only
//    wins while it is NEWER than the record it shadows, so deleting on one
//    device and editing on another resolves to whichever happened last.
// 3. COMPARE-AND-SWAP + retry. The write is conditional on the `updated_at` we
//    read, so two devices writing concurrently cannot interleave into a lost
//    update — the loser re-reads and re-merges.
// 4. DEBOUNCE + SERIALIZE. Previously every mutation fired its own full-blob
//    PUT. Writes are now coalesced and strictly one-at-a-time per table, which
//    is what makes the read-modify-write cycle safe (and cuts egress).

export interface SyncedRecord {
  id: string;
  updatedAt?: string;
}

/** id → deletedAt (ISO). Lives inside the blob as `data.deleted`. */
export type Tombstones = Record<string, string>;

/**
 * How long a tombstone is kept. It must outlive any plausible offline device,
 * or that device's stale copy would resurrect the record on its next sync; but
 * it must expire eventually or the map grows without bound.
 */
export const TOMBSTONE_TTL_MS = 90 * 24 * 60 * 60 * 1000; // 90 days

const time = (v?: string | null): number => {
  if (!v) return 0;
  const t = new Date(v).getTime();
  return Number.isNaN(t) ? 0 : t;
};

/** Record a deletion. Returns a new map (never mutates). */
export function tombstone(map: Tombstones | undefined, id: string, at?: string): Tombstones {
  return { ...(map ?? {}), [id]: at ?? new Date().toISOString() };
}

/** Union two tombstone maps; the LATER deletion wins for a shared id. */
export function mergeTombstones(a?: Tombstones, b?: Tombstones): Tombstones {
  const out: Tombstones = { ...(a ?? {}) };
  for (const [id, at] of Object.entries(b ?? {})) {
    if (time(at) > time(out[id])) out[id] = at;
  }
  return out;
}

/** Drop tombstones older than the TTL so the map cannot grow forever. */
export function pruneTombstones(map?: Tombstones, nowMs: number = Date.now()): Tombstones {
  const out: Tombstones = {};
  for (const [id, at] of Object.entries(map ?? {})) {
    if (nowMs - time(at) < TOMBSTONE_TTL_MS) out[id] = at;
  }
  return out;
}

/**
 * Union two collections by id, newest `updatedAt` wins, minus anything a
 * tombstone still shadows.
 *
 * Cloud wins on ties, matching the previous mergeRecords behaviour so a freshly
 * loaded device converges toward shared state by default.
 *
 * ⚠️ A tombstone only removes a record while the deletion is NEWER than the
 * record's own `updatedAt`. Otherwise deleting on device A would permanently
 * suppress a later edit (or a legitimate re-creation of the same id) on device B.
 */
export function mergeCollection<T extends SyncedRecord>(
  local: T[] | undefined,
  cloud: T[] | undefined,
  tombstones?: Tombstones
): T[] {
  const byId = new Map<string, T>();
  for (const item of local ?? []) byId.set(item.id, item);
  for (const c of cloud ?? []) {
    const existing = byId.get(c.id);
    if (!existing || time(c.updatedAt) >= time(existing.updatedAt)) byId.set(c.id, c);
  }
  const out: T[] = [];
  for (const item of byId.values()) {
    const deletedAt = tombstones?.[item.id];
    if (deletedAt && time(deletedAt) >= time(item.updatedAt)) continue;
    out.push(item);
  }
  return out;
}

/**
 * ⚠️⚠️ MOVING A STORE ONTO UNION WRITES. Its old load was cloud-wins, so a record
 * deleted on another device simply vanished from the next load — no tombstone
 * was ever written. A cache from before the move can still hold such records,
 * and the first union merge would bring every one of them back.
 *
 * `legacyIds` = the ids that pre-move cache held (recorded by the store's
 * persist `migrate`). Until one load has confirmed the cloud, a legacy record
 * the cloud lacks is treated as deleted elsewhere — exactly what the old load
 * did — while anything created since is kept. `null` = nothing to confirm.
 */
export function dropUnconfirmed<T extends { id: string }>(
  local: T[] | undefined,
  cloud: T[] | undefined,
  legacyIds: string[] | null | undefined,
): T[] {
  if (!legacyIds?.length) return local ?? [];
  const legacy = new Set(legacyIds);
  const inCloud = new Set((cloud ?? []).map(r => r.id));
  return (local ?? []).filter(r => !legacy.has(r.id) || inCloud.has(r.id));
}

/**
 * Same records, ignoring order. Stores use this after a merge to decide whether
 * to keep the PREVIOUS array reference — a no-op merge that returns a fresh
 * array would otherwise trip every `state.x === prev.x` subscription and fire a
 * pointless write on every boot.
 */
export function sameRecords<T extends SyncedRecord>(a: T[] | undefined, b: T[] | undefined): boolean {
  const left = a ?? [];
  const right = b ?? [];
  if (left.length !== right.length) return false;
  const key = (list: T[]) => list.map(r => `${r.id}@${r.updatedAt ?? ''}`).sort().join('|');
  return key(left) === key(right);
}

/**
 * Same deletions (ids and times). A load keeps the PREVIOUS map when its merge changed
 * none — `pruneTombstones` always hands back a new one, which tripped the sync
 * subscription into one more full read of the row on every boot.
 */
export function sameTombstones(a: Tombstones | undefined, b: Tombstones | undefined): boolean {
  const left = Object.entries(a ?? {});
  if (left.length !== Object.keys(b ?? {}).length) return false;
  return left.every(([id, at]) => b?.[id] === at);
}

/**
 * True when a merge produced exactly what the cloud already holds.
 *
 * Stores return `null` from `build` in that case so the cycle writes nothing.
 * This is what replaces the old `suppressXSync` flags: instead of blanket-muting
 * writes during initialize() — which also muted the legitimate write-backs and is
 * why the two sides could never converge — the write is skipped precisely when it
 * would be a no-op, and still happens when the merge really does hold something
 * the cloud is missing.
 */
export function matchesCloud(
  pairs: Array<[SyncedRecord[], unknown]>,
  mergedTombstones: Tombstones,
  cloudTombstones: Tombstones
): boolean {
  if (Object.keys(mergedTombstones).length !== Object.keys(cloudTombstones).length) return false;
  return pairs.every(([merged, cloud]) =>
    sameRecords(merged, Array.isArray(cloud) ? (cloud as SyncedRecord[]) : [])
  );
}

// ─── the write scheduler ────────────────────────────────────────────────────

export interface BlobSnapshot {
  updatedAt: string | null;
  data: any;
  /** The account the row was read for (see BlobSyncOptions.owner). */
  user?: string;
}

/**
 * A write's outcome. `true` or `{ updatedAt }` = accepted (the object also says
 * which revision the row is now at); false = the compare-and-swap lost (someone
 * else wrote first); `'skipped'` = signed out mid-cycle: nothing was written, and
 * nothing should be retried or treated as confirmed.
 */
export type BlobWriteResult = boolean | 'skipped' | { updatedAt: string | null };

/** Storage seam — swapped for a fake in tests. */
export interface BlobTransport {
  /** `null` means "not signed in" — the cycle skips. A signed-in user with no
   *  row yet returns `{updatedAt: null, data: null}`. Says whose row it read (`user`). */
  read(): Promise<BlobSnapshot | null>;
  /**
   * The row's `updated_at` ALONE (null = no row; a null result = signed out) — a
   * few bytes, so `refresh` can tell "nothing moved" without downloading the
   * blob. Optional: without it a refresh always reads the row.
   */
  version?(): Promise<{ updatedAt: string | null } | null>;
  /**
   * `user` = the account the row was read for: when another account is signed in
   * by the time the write goes out, nothing is written (`'skipped'`).
   */
  write(payload: any, expectedUpdatedAt: string | null, user?: string): Promise<BlobWriteResult>;
}

/** What a `refresh` did. */
export type RefreshOutcome =
  /** Not run: not loaded, signed out, another account, or no row to fold in. */
  | 'skipped'
  /** The row has not moved since this tab last held it — nothing was downloaded. */
  | 'unchanged'
  /** The row was read and folded into this tab. */
  | 'refreshed'
  /** The read failed (offline) — this tab keeps what it shows. */
  | 'failed';

export interface BlobSyncOptions {
  /** Builds the payload to store, given the CURRENT cloud data (null if no row). */
  build: (cloudData: any) => any | null;
  transport: BlobTransport;
  /**
   * ⚠️⚠️ WHOSE DATA THE STORE HOLDS — the account it was loaded for (`cachedUserId`);
   * null = not known yet (nothing loaded on this device).
   *
   * A sign-out does not reload the page, and a persisted store keeps the last
   * account's records until the next account's load replaces them. A write re-reads
   * the row of whoever is signed in WHEN IT RUNS and merges the store into it, so an
   * edit made by the next account before its load answered (or after the load
   * failed) used to merge the last account's records into the new account's row.
   * A cycle that reads another account's row than the owner's now writes nothing,
   * and folds nothing in; what the store holds stays on this device for its owner.
   */
  owner: () => string | null;
  debounceMs?: number;
  maxAttempts?: number;
  onError?: (error: unknown) => void;
  /**
   * Called with the payload once the database has ACCEPTED it — never for a
   * write that lost its compare-and-swap, and never when signed out. A store
   * that tracks "the last cloud copy I agree with" (syncDoc) advances it here.
   * `user` = the account it was written for. ⚠️ Not called when the store has
   * changed hands while the write was out: it confirms another account's copy.
   */
  onWritten?: (payload: unknown, user: string | undefined) => void;
}

export interface BlobSync {
  /** Request a write. Repeated calls inside the debounce window coalesce. */
  schedule: () => void;
  /** Await any pending + in-flight write (used by tests and explicit saves). */
  flush: () => Promise<void>;
  /**
   * Write NOW (no debounce) and resolve with whether it landed: true for a
   * confirmed write or nothing to write, false when it failed, gave up, or the
   * user is signed out. For a store that must tell the user "saved" or roll back.
   */
  saveNow: () => Promise<boolean>;
  /**
   * Run `task` on the same one-at-a-time chain as the writes. A store's LOAD
   * goes through here so a write cycle can never interleave with it — the load
   * would otherwise adopt a cloud copy older than the one the write just made.
   */
  exclusive: <T>(task: () => Promise<T>) => Promise<T>;
  /**
   * Catch this tab up with the cloud copy (a tab coming back into view — see
   * lib/blobFocusRefresh.ts). On the write chain, so a write in flight lands
   * first: `ready` decides whether to run at all (loaded, and for the account
   * signed in now); then the row's `updated_at` alone is asked for, and only
   * when it moved since this tab last held the cloud copy is the row read and
   * handed to `fold`. `fold` brings it into the store and returns true when the
   * tab still holds edits the cloud lacks — a write is then scheduled. No row
   * means nothing to fold in: the boot load and the next write deal with that.
   *
   * ⚠️ "Last held" is tracked from this sync's own reads and writes, so a store
   * that refreshes must ADOPT the cloud copy whenever its `build` returns null
   * and in `onWritten` (syncDoc does; so does the AI chat store), and report its
   * own loads through `noteLoaded`.
   */
  refresh: (
    fold: (cloudData: unknown) => boolean,
    ready?: () => boolean | Promise<boolean>,
  ) => Promise<RefreshOutcome>;
  /** A load read the row at `updatedAt` and brought it into the store (null = unknown). */
  noteLoaded: (updatedAt: string | null) => void;
  /**
   * ⚠️⚠️ A load has just merged the cloud copy (`cloudData`; null = no row) into the
   * store: schedule a write when the store holds anything that copy lacks — decided by
   * `build` against the CLOUD copy, not by whether the merge changed what this device
   * held. A merge equal to the device's copy (records whose write was skipped while
   * signed out, a new account's first records with no row yet) changes no reference, so
   * the store's subscription never saw it and it waited for the account's next change.
   * For a collection store whose `build` only reads (syncDoc's `reconcile` covers documents).
   */
  writeBack: (cloudData: unknown) => void;
}

// Debouncing means a mutation made a moment before the tab closes could miss its
// write. Local state survives in localStorage either way (the next boot's
// read-modify-write pushes it), but flushing on the way out keeps other devices
// current rather than making them wait for this one to reopen.
const liveSyncs = new Set<BlobSync>();
let unloadHookInstalled = false;

export function flushAllBlobSyncs(): Promise<void[]> {
  return Promise.all([...liveSyncs].map(s => s.flush()));
}

function installUnloadHook(): void {
  if (unloadHookInstalled || typeof window === 'undefined') return;
  unloadHookInstalled = true;
  // `pagehide` fires on mobile Safari's bfcache path where `beforeunload` does not.
  window.addEventListener('pagehide', () => { void flushAllBlobSyncs(); });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void flushAllBlobSyncs();
  });
}

export function createBlobSync(options: BlobSyncOptions): BlobSync {
  const { build, transport, owner, debounceMs = 400, maxAttempts = 3, onError, onWritten } = options;

  let timer: ReturnType<typeof setTimeout> | null = null;
  let pending = false;
  // Every cycle appends to this chain, so a table never has two writes in
  // flight — without that, two read-modify-write cycles could interleave and
  // reintroduce exactly the lost update this module exists to prevent.
  let chain: Promise<void> = Promise.resolve();
  // Whether the most recent cycle ended with the cloud holding this tab's state.
  let lastOk = true;
  // The row revision (`updated_at`) this tab last held the cloud copy of; null =
  // unknown. A refresh that finds the row still there downloads nothing.
  let caughtUp: string | null = null;

  /** The row read is the store's owner's (or the store has none yet). */
  const ownRow = (snapshot: BlobSnapshot): boolean => {
    const holder = owner();
    return holder === null || holder === snapshot.user;
  };

  async function cycle(): Promise<boolean> {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const snapshot = await transport.read();
      if (snapshot === null) return false; // signed out — nothing to sync
      // Another account is signed in: what the store holds is not theirs to write.
      if (!ownRow(snapshot)) return false;
      const payload = build(snapshot.data);
      if (payload === null || payload === undefined) {
        caughtUp = snapshot.updatedAt; // nothing to write: the store took the cloud's copy
        return true;
      }
      const written = await transport.write(payload, snapshot.updatedAt, snapshot.user);
      if (written === 'skipped') return false;
      if (written) {
        // The store changed hands while the write was out: the confirmation (and
        // the revision) belong to the account written for, not to what it holds now.
        if (!ownRow(snapshot)) return true;
        caughtUp = typeof written === 'object' ? written.updatedAt : null;
        onWritten?.(payload, snapshot.user);
        return true;
      }
      // CAS lost: another writer landed between our read and our write. Loop —
      // the next read picks up their data and re-merges ours on top.
    }
    onError?.(new Error('sync: gave up after concurrent writes'));
    return false;
  }

  function run(): void {
    pending = false;
    chain = chain.then(() => cycle().then(
      (ok) => { lastOk = ok; },
      (err) => { lastOk = false; onError?.(err); },
    ));
  }

  const sync: BlobSync = {
    schedule() {
      pending = true;
      if (timer) clearTimeout(timer);
      timer = setTimeout(run, debounceMs);
    },
    async flush() {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      if (pending) run();
      await chain;
    },
    saveNow() {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
      run();
      return chain.then(() => lastOk);
    },
    exclusive<T>(task: () => Promise<T>): Promise<T> {
      const result = chain.then(task);
      // The chain itself must survive a failed task, or every later write
      // would be skipped; the caller still sees the failure through `result`.
      chain = result.then(() => undefined, () => undefined);
      return result;
    },
    refresh(fold, ready) {
      return sync.exclusive(async (): Promise<RefreshOutcome> => {
        try {
          if (ready && !(await ready())) return 'skipped';
          if (caughtUp !== null && transport.version) {
            const probe = await transport.version();
            if (probe === null || probe.updatedAt === null) return 'skipped';
            if (probe.updatedAt === caughtUp) return 'unchanged';
          }
          const snapshot = await transport.read();
          // Signed out — or another account's row: never folded into this store.
          if (snapshot === null || !ownRow(snapshot)) return 'skipped';
          if (snapshot.data === null || snapshot.data === undefined) {
            // Nothing to fold in — the boot load and the next write deal with
            // that. The revision still counts as held: a row holding nothing for
            // this store (a preference column never saved, on a row other columns
            // keep moving) would otherwise be downloaded again on every focus.
            caughtUp = snapshot.updatedAt;
            return 'skipped';
          }
          const unsaved = fold(snapshot.data);
          caughtUp = snapshot.updatedAt;
          if (unsaved) sync.schedule();
          return 'refreshed';
        } catch (error) {
          onError?.(error);
          return 'failed';
        }
      });
    },
    noteLoaded(updatedAt) {
      caughtUp = updatedAt;
    },
    writeBack(cloudData) {
      const payload = build(cloudData ?? null);
      if (payload !== null && payload !== undefined) sync.schedule();
    },
  };

  liveSyncs.add(sync);
  installUnloadHook();
  return sync;
}
